import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Users,
  Calendar,
  MapPin,
  Star,
  ArrowRight,
  CheckCircle,
  Clock,
  Heart,
  Bell,
  UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to check if a date is in the future
const isFutureDate = (dateStr) => {
  // Parse dates like "September 28, 2025" or "February 2026"
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) {
    // Try parsing "Month Year" format
    const parts = dateStr.split(' ');
    if (parts.length === 2) {
      const monthYear = new Date(`${parts[0]} 1, ${parts[1]}`);
      return monthYear > new Date();
    }
    return true; // If can't parse, show it
  }
  return parsed > new Date();
};

// Scheduled group tour dates from the actual website (filtered for future dates)
const allScheduledDates = {
  'cruise-the-nile': [
    { date: 'September 28, 2025', status: 'open', spotsLeft: 8 },
    { date: 'March 29, 2026', status: 'open', spotsLeft: 12 },
    { date: 'September 27, 2026', status: 'open', spotsLeft: 12 }
  ],
  'carnaval-in-color': [
    { date: 'February 2026', status: 'coming_soon', spotsLeft: null },
    { date: 'February 2027', status: 'coming_soon', spotsLeft: null }
  ],
  'red-sea-retreat': [
    { date: 'September 28, 2025', status: 'open', spotsLeft: 10 },
    { date: 'March 29, 2026', status: 'open', spotsLeft: 12 },
    { date: 'September 27, 2026', status: 'open', spotsLeft: 12 }
  ],
  'vila-flor-shores': [
    { date: 'September 28, 2025', status: 'open', spotsLeft: 6 },
    { date: 'March 29, 2026', status: 'open', spotsLeft: 12 },
    { date: 'September 27, 2026', status: 'open', spotsLeft: 12 }
  ]
};

// Filter to only future dates
const scheduledDates = Object.fromEntries(
  Object.entries(allScheduledDates).map(([slug, dates]) => [
    slug,
    dates.filter(d => isFutureDate(d.date))
  ])
);

// Mock solo traveler interest counts (in production, this comes from DB)
const soloTravelerInterest = {
  'cruise-the-nile': 12,
  'red-sea-retreat': 8,
  'carnaval-in-color': 15,
  'vila-flor-shores': 6
};

const whyGroupTravel = [
  {
    icon: UserPlus,
    title: "No Travel Partner? No Problem",
    description: "Don't let not having someone to travel with hold you back. Join a group of like-minded adventurers and make new friends along the way."
  },
  {
    icon: Heart,
    title: "Solo But Not Alone",
    description: "Want to travel independently but prefer the comfort of a group? Get the best of both worlds — freedom with a built-in community."
  },
  {
    icon: Users,
    title: "Shared Experiences",
    description: "Some moments are better shared. Create lasting memories with fellow travelers who become lifelong friends."
  },
  {
    icon: CheckCircle,
    title: "Everything Handled",
    description: "No planning stress. We organize everything — you just show up and enjoy the adventure."
  }
];

export default function GroupAdventuresPage() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistTrip, setWaitlistTrip] = useState(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const res = await axios.get(`${API}/trips`);
      // Add scheduled dates to trips
      const tripsWithDates = res.data.map(trip => ({
        ...trip,
        scheduledDates: scheduledDates[trip.slug] || []
      }));
      setTrips(tripsWithDates);
    } catch (err) {
      console.error('Failed to fetch trips:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail || !waitlistTrip) return;

    setWaitlistLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: waitlistEmail,
        trip_slug: waitlistTrip.slug,
        trip_title: waitlistTrip.title
      });
      toast.success('Added to waitlist! We\'ll notify you when spots open.');
      setWaitlistEmail('');
      setWaitlistTrip(null);
    } catch (err) {
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const getStatusBadge = (status, spotsLeft) => {
    switch (status) {
      case 'open':
        return spotsLeft <= 4 ? (
          <Badge className="bg-orange-500 text-white">Only {spotsLeft} spots left!</Badge>
        ) : (
          <Badge className="bg-green-500 text-white">Booking Open</Badge>
        );
      case 'full':
        return <Badge className="bg-red-500 text-white">Sold Out - Waitlist</Badge>;
      case 'coming_soon':
        return <Badge className="bg-purple-500 text-white">Coming Soon</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-orange-50 to-transparent pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Users className="h-4 w-4" />
            Scheduled Group Tours
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Group Adventures
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Our scheduled group tours run throughout the year with set departure dates. Perfect for travelers 
            who want a hassle-free adventure with built-in community — whether you're flying solo, bringing 
            a friend, or looking for a ready-made travel family.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Why Group Travel */}
        <section className="mb-16">
          <h2 className="font-heading text-2xl font-semibold text-primary text-center mb-8">
            Why Travel With a Group?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {whyGroupTravel.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className="h-full text-center">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                      <item.icon className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-primary text-sm mb-2">{item.title}</h3>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Upcoming Tours with Dates */}
        <section>
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-primary mb-4">
              Upcoming Scheduled Tours
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Browse our scheduled departure dates below. Groups are kept small (8-15 travelers) for a 
              more intimate experience.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" />
            </div>
          ) : (
            <div className="space-y-8">
              {trips.filter(t => t.scheduledDates && t.scheduledDates.length > 0).map((trip, idx) => (
                <motion.div
                  key={trip.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <Card className="overflow-hidden">
                    <div className="flex flex-col lg:flex-row">
                      {/* Trip Image */}
                      <div className="lg:w-1/3 relative">
                        <img
                          src={trip.image_url}
                          alt={trip.title}
                          className="w-full h-48 lg:h-full object-cover"
                        />
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-orange-500 text-white">
                            <Users className="h-3 w-3 mr-1" />
                            Group Tour
                          </Badge>
                        </div>
                      </div>

                      {/* Trip Info & Dates */}
                      <div className="flex-1 p-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                          <div>
                            <h3 className="font-heading text-xl font-semibold text-primary mb-1">
                              {trip.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {trip.destination}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {trip.duration}
                              </span>
                              <span className="flex items-center gap-1">
                                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                                {trip.rating}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-heading font-bold text-secondary">
                              ${trip.price.toLocaleString()}
                            </span>
                            <span className="text-sm text-muted-foreground block">per person</span>
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {trip.description}
                        </p>

                        {/* Solo Traveler Interest Banner */}
                        {soloTravelerInterest[trip.slug] > 0 && (
                          <div className="mb-4 p-3 bg-cyan-50 border border-cyan-200 rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Heart className="h-4 w-4 text-cyan-600" />
                              <span className="text-sm text-cyan-800">
                                <strong>{soloTravelerInterest[trip.slug]}</strong> solo travelers are looking for travel partners for this trip
                              </span>
                            </div>
                            <Link to="/solo-adventures">
                              <Button size="sm" variant="ghost" className="text-cyan-700 hover:text-cyan-900 text-xs">
                                Find a Match
                                <ArrowRight className="h-3 w-3 ml-1" />
                              </Button>
                            </Link>
                          </div>
                        )}

                        {/* Scheduled Dates */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Upcoming Departure Dates
                          </h4>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {trip.scheduledDates.map((schedule, i) => (
                              <div 
                                key={i} 
                                className={`p-3 rounded-lg border ${
                                  schedule.status === 'full' 
                                    ? 'bg-gray-50 border-gray-200' 
                                    : 'bg-green-50 border-green-200'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-sm">{schedule.date}</span>
                                  {getStatusBadge(schedule.status, schedule.spotsLeft)}
                                </div>
                                {schedule.status === 'open' && (
                                  <Link to={`/trips/${trip.slug}?date=${encodeURIComponent(schedule.date)}`}>
                                    <Button size="sm" className="w-full mt-2 bg-secondary hover:bg-secondary/90">
                                      Book This Date
                                    </Button>
                                  </Link>
                                )}
                                {schedule.status === 'full' && (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="w-full mt-2"
                                    onClick={() => setWaitlistTrip({ ...trip, selectedDate: schedule.date })}
                                  >
                                    <Bell className="h-3 w-3 mr-1" />
                                    Join Waitlist
                                  </Button>
                                )}
                                {schedule.status === 'coming_soon' && (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="w-full mt-2"
                                    onClick={() => setWaitlistTrip({ ...trip, selectedDate: schedule.date })}
                                  >
                                    <Bell className="h-3 w-3 mr-1" />
                                    Notify Me
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t flex items-center justify-between">
                          <Link to={`/trips/${trip.slug}`}>
                            <Button variant="link" className="text-secondary p-0">
                              View Full Itinerary
                              <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                          </Link>
                          <span className="text-xs text-muted-foreground">
                            Small groups of 8-15 travelers
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Waitlist Modal */}
        {waitlistTrip && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-secondary" />
                  Join Waitlist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Get notified when spots open up for <strong>{waitlistTrip.title}</strong>
                  {waitlistTrip.selectedDate && ` (${waitlistTrip.selectedDate})`}
                </p>
                <form onSubmit={handleWaitlist} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-1 block">Email Address</Label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setWaitlistTrip(null)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-secondary hover:bg-secondary/90"
                      disabled={waitlistLoading}
                    >
                      {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Custom Dates CTA */}
        <div className="mt-16 text-center">
          <Card className="bg-muted/50">
            <CardContent className="py-8">
              <h3 className="font-heading text-xl font-semibold text-primary mb-2">
                Dates Don't Work?
              </h3>
              <p className="text-muted-foreground mb-4 max-w-lg mx-auto">
                We can run any itinerary as a private group on dates that work for you and your crew.
              </p>
              <Link to="/customize">
                <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary/10">
                  Request Custom Dates
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
